import React from "react";

// Stylised benzene-ring / hex molecule
export const MoleculeHex = ({ size = 80, className = "", color = "#0d9488", opacity = 0.5 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-hidden="true">
    <g fill="none" stroke={color} strokeWidth="2" opacity={opacity}>
      <polygon points="50,8 86,28 86,72 50,92 14,72 14,28" />
      <polygon points="50,22 76,36 76,64 50,78 24,64 24,36" />
      <line x1="50" y1="8" x2="50" y2="22" />
      <line x1="86" y1="28" x2="76" y2="36" />
      <line x1="86" y1="72" x2="76" y2="64" />
      <line x1="50" y1="92" x2="50" y2="78" />
      <line x1="14" y1="72" x2="24" y2="64" />
      <line x1="14" y1="28" x2="24" y2="36" />
    </g>
    <g fill={color} opacity={opacity}>
      <circle cx="50" cy="8" r="3" />
      <circle cx="86" cy="28" r="3" />
      <circle cx="86" cy="72" r="3" />
      <circle cx="50" cy="92" r="3" />
      <circle cx="14" cy="72" r="3" />
      <circle cx="14" cy="28" r="3" />
    </g>
  </svg>
);

// Stylised atom with orbits
export const Atom = ({ size = 90, className = "", color = "#0d9488", opacity = 0.6 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-hidden="true">
    <g stroke={color} strokeWidth="2" fill="none" opacity={opacity}>
      <ellipse cx="50" cy="50" rx="44" ry="18" />
      <ellipse cx="50" cy="50" rx="44" ry="18" transform="rotate(60 50 50)" />
      <ellipse cx="50" cy="50" rx="44" ry="18" transform="rotate(120 50 50)" />
    </g>
    <circle cx="50" cy="50" r="5" fill={color} opacity={opacity + 0.2} />
  </svg>
);

// Small water droplet
export const Droplet = ({ size = 40, color = "#0ea5e9", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 32" className={className} aria-hidden="true">
    <path d="M12 0 C12 0 22 12 22 21 a10 10 0 1 1 -20 0 C2 12 12 0 12 0 Z" fill={color} opacity="0.85" />
    <ellipse cx="9" cy="18" rx="3" ry="4" fill="#ffffff" opacity="0.55" />
  </svg>
);

// Background molecule field — absolutely positioned floating molecules
export const MoleculeField = ({ density = 6, className = "" }) => {
  const items = Array.from({ length: density });
  const positions = [
    { top: "6%", left: "4%", size: 110, anim: "float-slow", op: 0.18, rev: false },
    { top: "12%", right: "6%", size: 80, anim: "float-med", op: 0.16, rev: true },
    { top: "55%", left: "8%", size: 64, anim: "float-fast", op: 0.14, rev: false },
    { top: "68%", right: "10%", size: 100, anim: "float-slow", op: 0.18, rev: true },
    { top: "36%", left: "50%", size: 56, anim: "float-med", op: 0.12, rev: false },
    { bottom: "8%", left: "38%", size: 72, anim: "float-fast", op: 0.15, rev: true },
  ];
  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`} aria-hidden="true">
      {items.map((_, i) => {
        const p = positions[i % positions.length];
        const isAtom = i % 3 === 2;
        return (
          <div
            key={i}
            className={p.anim}
            style={{
              position: "absolute",
              top: p.top,
              left: p.left,
              right: p.right,
              bottom: p.bottom,
            }}
          >
            {isAtom ? (
              <div className={p.rev ? "atom-spin-rev" : "atom-spin"}>
                <Atom size={p.size} opacity={p.op} />
              </div>
            ) : (
              <MoleculeHex size={p.size} opacity={p.op} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// Bubble field overlay
export const Bubbles = ({ count = 12, className = "" }) => {
  const rng = (a, b) => a + Math.random() * (b - a);
  const items = Array.from({ length: count }).map((_, i) => ({
    left: rng(2, 96),
    size: rng(6, 22),
    delay: rng(0, 6),
    dur: rng(4.5, 8.5),
    op: rng(0.35, 0.8),
  }));
  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`} aria-hidden="true">
      {items.map((b, i) => (
        <span
          key={i}
          className="bubble"
          style={{
            left: `${b.left}%`,
            width: `${b.size}px`,
            height: `${b.size}px`,
            opacity: b.op,
            animationDelay: `${b.delay}s`,
            animationDuration: `${b.dur}s`,
          }}
        />
      ))}
    </div>
  );
};

// Animated beaker/flask SVG with liquid fill
export const AnimatedBeaker = ({ className = "", fillPercent = 65 }) => (
  <div className={`relative inline-block ${className}`} aria-hidden="true">
    <svg width="160" height="200" viewBox="0 0 160 200">
      <defs>
        <linearGradient id="liq" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5eead4" />
          <stop offset="100%" stopColor="#0891b2" />
        </linearGradient>
        <clipPath id="flaskClip">
          <path d="M60 30 L100 30 L100 70 L138 165 C142 178 132 190 119 190 L41 190 C28 190 18 178 22 165 L60 70 Z" />
        </clipPath>
      </defs>
      {/* Flask outline */}
      <path d="M60 30 L100 30 L100 70 L138 165 C142 178 132 190 119 190 L41 190 C28 190 18 178 22 165 L60 70 Z"
            fill="none" stroke="#0f766e" strokeWidth="3" strokeLinejoin="round" />
      <line x1="55" y1="30" x2="105" y2="30" stroke="#0f766e" strokeWidth="4" strokeLinecap="round" />
      {/* Liquid */}
      <g clipPath="url(#flaskClip)">
        <rect x="0" y={`${200 - (fillPercent * 1.6)}`} width="160" height="200" fill="url(#liq)" />
        {/* Wave on liquid surface */}
        <path d={`M0 ${200 - (fillPercent * 1.6)} Q 40 ${190 - (fillPercent * 1.6)} 80 ${200 - (fillPercent * 1.6)} T 160 ${200 - (fillPercent * 1.6)} V 200 H 0 Z`} fill="#ffffff" opacity="0.18" />
      </g>
      {/* Bubbles inside */}
      <g clipPath="url(#flaskClip)">
        <circle cx="60" cy="160" r="3" fill="#ffffff" opacity="0.7">
          <animate attributeName="cy" from="185" to="110" dur="3.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.8;0" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="95" cy="170" r="2.4" fill="#ffffff" opacity="0.6">
          <animate attributeName="cy" from="185" to="100" dur="4s" begin="0.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.7;0" dur="4s" begin="0.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="78" cy="175" r="3.2" fill="#ffffff" opacity="0.5">
          <animate attributeName="cy" from="185" to="95" dur="3.6s" begin="1.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.6;0" dur="3.6s" begin="1.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  </div>
);

// Decorative SVG wave divider
export const WaveDivider = ({ flip = false, color = "#ffffff", from = "#f8fafc" }) => (
  <div className="relative w-full leading-[0]" style={{ transform: flip ? "rotate(180deg)" : "none" }} aria-hidden="true">
    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-[60px]" style={{ background: from }}>
      <path d="M0,40 C240,80 480,0 720,30 C960,60 1200,20 1440,50 L1440,80 L0,80 Z" fill={color} />
    </svg>
  </div>
);
